import { AlertTriangle, CheckCircle2, Info, Loader2, X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Button } from "@/components/ui";

export type ToastTone = "info" | "success" | "warning";

export interface ToastInput {
  /** Showing a toast again with the same id replaces it, so a running process keeps one toast up to date. */
  id?: string;
  tone?: ToastTone;
  title: string;
  body?: ReactNode;
  /** A spinner instead of the tone's icon: the thing it's about is still running. */
  busy?: boolean;
  /** Milliseconds before it closes itself; `null` keeps it until it's closed or dismissed. */
  duration?: number | null;
  /** A button in the toast ("Undo"); using it closes the toast. */
  action?: { label: string; onClick: () => void };
}

interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  body?: ReactNode;
  busy: boolean;
  duration: number | null;
  action?: { label: string; onClick: () => void };
}

export interface ToastApi {
  show: (toast: ToastInput) => string;
  dismiss: (id: string) => void;
}

const DEFAULT_DURATION = 6000;
const ToastContext = createContext<ToastApi | null>(null);
let counter = 0;

const ICONS: Record<ToastTone, ReactNode> = {
  info: <Info className="size-[18px] text-sky-600" aria-hidden />,
  success: <CheckCircle2 className="size-[18px] text-emerald-600" aria-hidden />,
  warning: <AlertTriangle className="size-[18px] text-amber-600" aria-hidden />,
};

/**
 * Short notes in the bottom corner about something that happened or is still happening in the
 * background (a competitor's first scan). Mounted once, around the router.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: string) => setToasts((all) => all.filter((t) => t.id !== id)), []);
  const show = useCallback((input: ToastInput) => {
    const toast: Toast = {
      id: input.id ?? `toast-${++counter}`,
      tone: input.tone ?? "info",
      title: input.title,
      body: input.body,
      busy: input.busy ?? false,
      duration: input.duration === undefined ? DEFAULT_DURATION : input.duration,
      action: input.action,
    };
    setToasts((all) =>
      all.some((t) => t.id === toast.id) ? all.map((t) => (t.id === toast.id ? toast : t)) : [...all, toast],
    );
    return toast.id;
  }, []);
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col items-end gap-2 sm:left-auto sm:w-[380px]"
      >
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} dismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, dismiss }: { toast: Toast; dismiss: (id: string) => void }) {
  // a replaced toast starts its time again
  useEffect(() => {
    if (toast.duration === null) return;
    const timer = window.setTimeout(() => dismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timer);
  }, [toast, dismiss]);

  return (
    <div
      role="status"
      className="pointer-events-auto flex w-full items-start gap-3 rounded-xl border border-border bg-surface px-4 py-3 text-fg shadow-lg"
    >
      <span className="mt-px shrink-0">
        {toast.busy ? (
          <Loader2 className="size-[18px] animate-spin text-brand-500" aria-hidden />
        ) : (
          ICONS[toast.tone]
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm font-semibold">{toast.title}</p>
        {toast.body && <div className="text-[13px] leading-relaxed text-fg-muted">{toast.body}</div>}
      </div>
      {toast.action && (
        <Button
          unstyled
          type="button"
          onClick={() => {
            toast.action?.onClick();
            dismiss(toast.id);
          }}
          className="shrink-0 self-center text-[13px] font-semibold text-brand-500 hover:text-brand-600"
        >
          {toast.action.label}
        </Button>
      )}
      <Button
        unstyled
        type="button"
        onClick={() => dismiss(toast.id)}
        className="-mr-1 shrink-0 rounded p-1 text-fg-subtle hover:bg-muted"
        aria-label="Dismiss"
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}

/** Outside a provider (a component rendered on its own) toasts go nowhere. */
const NOWHERE: ToastApi = { show: (t) => t.id ?? "", dismiss: () => undefined };

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NOWHERE;
}
